import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Eye, EyeOff, Copy, Plus, Trash2, ChevronDown, ChevronRight, PlayCircle, RefreshCw, MapPin, X, Check, Info } from 'lucide-react';
import { getToken, api } from '../lib/api';
import { toE164 } from '../lib/phone';
import { trunkHasSms } from '../lib/trunkSms';
import { useToast } from '../components/Toast';
import { SmsLanePicker, QuickLane, DedicatedLane, CancelSms, MasterConfigPanel } from '../components/SmsProvision';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// FreeSWITCH sofia gateway identifiers must be free of spaces and special
// characters. This mirrors the slugify in the /api/trunks backend handler so
// the form can show the user exactly what the technical ID will become.
function gwSlug(label: string): string {
  return (label || '').replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
}

// Provider definitions are NOT hardcoded here — they come from the .pv.xml
// templates in api/lib/trunk_templates/ via GET /api/trunk-templates. One
// source of truth for all 23+ carriers.

type Tab = 'general' | 'dids' | 'sms' | 'options' | 'test' | 'e911';

export default function AddTrunk() {
  const { provider, id } = useParams<{ provider: string; id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('general');
  // SMS setup (creds + Twilio auto-config) is system-admin-only.
  const isSuper = ['super_admin', 'reseller'].includes(api.me()?.role || '');
  const [form, setForm] = useState<any>({
    name: '',
    domain_uuid: '',
    default_route: 'end_call',
    create_outbound_rule: true,
    main_trunk_number: '',
    auth_id: '',
    auth_password: '',
    registrar: '',
    registrar_port: 5060,
    auto_discovery: false,
    dids: [],
    sms_enabled: false,
    sms_account_sid: '',
    sms_auth_token: '',
    sms_caller_id_match_digits: 3,
    sms_enable_caller_id_match: false,
    sim_calls: 10,
    limit_to: 'system_wide',
    allow_inbound: true,
    allow_outbound: true,
    diversion_header: true,
    sip_refer_xfer: false,
    // Standard enum strings (migration 013)
    matching_strategy_enum: 'MatchAnyFields',
    srtp_mode_enum: 'SRTPDisabled',
    require_auth_for_enum: 'Both',
    gateway_type: 'Provider',
    ip_in_registration_contact: 'External',
    disable_call_transfer: false,
  });
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  // Edit mode — load the existing trunk so the form can be prefilled.
  const existing = useQuery<any>({
    queryKey: ['trunk', id],
    queryFn: () => fJson(`/api/trunks/${id}`),
    enabled: isEdit,
  });
  // Prefill the form once when the trunk loads. We use a ref instead of
  // depending on existing.data so a later refetch (e.g. invalidate after save)
  // can't blow away unsaved edits.
  const prefillDone = useRef(false);
  useEffect(() => {
    if (!isEdit) return;
    const g = existing.data;
    if (!g || prefillDone.current) return;
    prefillDone.current = true;
    // GET /api/trunks/:id returns the unified flat shape (native v_gateways
    // columns + trunk_settings merged).
    setForm((f: any) => ({
      ...f,
      name: g.name || g.description || g.gateway_name || '',
      domain_uuid: g.domain_uuid || '',
      registrar: g.registrar || g.gateway || '',
      registrar_port: g.registrar_port || 5060,
      auth_id: g.auth_id || g.username || '',
      auth_password: g.auth_password || g.password || '',
      outbound_proxy: g.outbound_proxy || '',
      main_trunk_number: g.main_trunk_number || '',
      sim_calls: parseInt(g.sim_calls, 10) || 10,
      transport_restriction: g.transport_restriction || 'udp',
      default_route: g.default_route || 'end_call',
      limit_to: g.limit_to || 'system_wide',
      allow_inbound: g.allow_inbound !== false,
      allow_outbound: g.allow_outbound !== false,
      diversion_header: g.diversion_header !== false,
      sip_refer_xfer: !!g.sip_refer_xfer,
      auto_discovery: !!g.auto_discovery,
      dids: (g.dids || []).map((d: any) => (typeof d === 'string' ? { number: d, name: '', route: '' } : { route: '', ...d })),
      sms_enabled: !!(g.sms && g.sms.enabled),
      sms_account_sid: (g.sms && g.sms.account_sid) || '',
      sms_auth_token: (g.sms && g.sms.auth_token) || '',
    }));
  }, [existing.data, isEdit]);
  // Surface a load failure (silent 401/500 was hiding the cause).
  useEffect(() => {
    if (isEdit && existing.isError) {
      toast('error', `Couldn't load this trunk: ${(existing.error as any)?.message || 'unknown'}`);
    }
  }, [existing.isError]);

  // Provider templates — the single source of truth. The :provider route param
  // carries the template filename (e.g. "twilio.pv.xml"); in edit mode we match
  // on the stored template_filename / provider name.
  const templates = useQuery<any>({ queryKey: ['trunk-templates'], queryFn: () => fJson('/api/trunk-templates') });
  const allTpl: any[] = [...((templates.data || {}).builtin || []), ...((templates.data || {}).custom || [])];
  const tpl = allTpl.find(t => t.filename === provider)
    || allTpl.find(t => t.filename === (existing.data && existing.data.settings && existing.data.settings.template_filename))
    || allTpl.find(t => (t.name || '').toLowerCase() === String((existing.data && existing.data.provider) || '').toLowerCase())
    || null;
  const preset = {
    label: tpl ? tpl.name : 'Generic SIP Trunk',
    config_guide_url: tpl ? tpl.url : '',
    // Existing trunks that don't resolve a template still get the SMS tab when the
    // registrar/provider identify a Twilio trunk. See lib/trunkSms.
    has_sms: trunkHasSms(
      !!tpl && (tpl.tags || []).includes('sms'),
      form.registrar,
      existing.data && existing.data.provider,
    ),
    registrar_placeholder: (tpl && tpl.fields && tpl.fields.ProxyHost) || 'sip.provider.com',
  };
  const providerKey = (provider || (tpl && tpl.filename) || '').replace(/\.pv\.xml$/, '')
    || (existing.data && existing.data.provider) || 'generic';
  // One editor, all the trunk panels — Test & E911 fold in from the retired
  // standalone Trunks page (edit mode only, since they need a saved trunk).
  const tabs: [Tab, string][] = [
    ['general', 'General'] as [Tab, string],
    ['dids', 'DID Numbers'] as [Tab, string],
    ...(preset.has_sms ? [['sms', 'SMS'] as [Tab, string]] : []),
    ['options', 'Options'] as [Tab, string],
    ...(isEdit ? [['test', 'Test'] as [Tab, string], ['e911', "E911 / Kari's Law"] as [Tab, string]] : []),
  ];

  // Prefill the form from the template's device fields (new trunk only, once).
  const tplApplied = useRef(false);
  useEffect(() => {
    if (isEdit || !tpl || tplApplied.current) return;
    tplApplied.current = true;
    const fld = tpl.fields || {};
    setForm((f: any) => ({
      ...f,
      registrar: f.registrar || fld.ProxyHost || '',
      registrar_port: parseInt(fld.ProxyPort, 10) || 5060,
      transport_restriction: String(fld.TransportRestriction || 'udp').toLowerCase(),
      expire_seconds: parseInt(fld.TimeBetweenRegistration, 10) || 600,
      diversion_header: fld.DisableDiversionHeader !== '1',
    }));
  }, [tpl, isEdit]);

  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });

  const save = useMutation({
    mutationFn: async () => {
      // Step 1: create or update the trunk row.
      const created: any = await fJson(isEdit ? `/api/trunks/${id}` : '/api/trunks', {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          provider: providerKey,
          template_filename: tpl ? tpl.filename : undefined,
          // Non-secret SMS flags only. Twilio creds are NOT stored on the trunk
          // (that JSON was plaintext) — they go, encrypted, via the sys-admin
          // provision endpoint below (which also auto-configures Twilio).
          sms: {
            enabled: !!form.sms_enabled,
            caller_id_match: !!form.sms_enable_caller_id_match,
            caller_id_digits: form.sms_caller_id_match_digits,
          },
        }),
      });
      // Step 2: persist the migration-013 enum-string security fields against trunk_settings.
      const tid = created.gateway_uuid || created.id || id;
      if (tid) {
        try {
          await fJson(`/api/trunks-extended/${tid}/settings`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              matching_strategy_enum: form.matching_strategy_enum,
              srtp_mode_enum: form.srtp_mode_enum,
              require_auth_for_enum: form.require_auth_for_enum,
              gateway_type: form.gateway_type,
              ip_in_registration_contact: form.ip_in_registration_contact,
              disable_call_transfer: form.disable_call_transfer,
            }),
          });
        } catch { /* settings are non-fatal; trunk row is the source of truth */ }
      }
      // Step 3: SMS provisioning (sys-admin). Stores creds encrypted, mints the
      // webhook, and auto-configures Twilio's SmsUrl for this trunk's DIDs.
      // Blank creds → the endpoint reuses the department's saved credentials.
      if (tid && isSuper) {
        const numbers = (form.dids || []).map((d: any) => toE164(d.number || d)).filter(Boolean);
        try {
          const pr: any = await fJson(`/api/sms/trunk/${tid}/provision`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              enabled: !!form.sms_enabled,
              account_sid: form.sms_account_sid,
              auth_token: form.sms_auth_token,
              from_number: numbers[0] || '',
              numbers,
            }),
          });
          created.__sms = pr;
        } catch (e: any) { created.__smsError = e.message || String(e); }
      }
      return created;
    },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['voice-chat-list'] });
      qc.invalidateQueries({ queryKey: ['trunk', id] });
      qc.invalidateQueries({ queryKey: ['did-inventory'] });
      qc.invalidateQueries({ queryKey: ['sms-config'] });
      const didCount = (form.dids || []).length;
      toast('success', `${isEdit ? 'Trunk updated' : 'Trunk created'}${didCount ? ` · ${didCount} DID${didCount === 1 ? '' : 's'} saved` : ''}`);
      if (r.__smsError) toast('error', `SMS: ${r.__smsError}`);
      else if (r.__sms && form.sms_enabled) {
        const ok = (r.__sms.provisioned || []).length, bad = (r.__sms.errors || []).length;
        if (bad) toast('error', `SMS on for ${ok} number(s); ${bad} failed to auto-configure in Twilio`);
        else if (ok) toast('success', `SMS enabled + auto-configured in Twilio for ${ok} number(s)`);
      }
      navigate('/voice-chat');
    },
    onError: (e: any) => toast('error', e.message),
  });

  // Delete this trunk. The backend also removes its DID inbound rules (#249), so
  // the confirm spells that out — inbound to those DIDs stops until re-provisioned.
  const del = useMutation({
    mutationFn: () => fJson(`/api/trunks/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('success', 'Trunk deleted');
      qc.invalidateQueries({ queryKey: ['voice-chat-list'] });
      navigate('/voice-chat');
    },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/voice-chat')} className="btn-ghost inline-flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
        <h1 className="text-xl font-semibold">{isEdit ? 'Edit Trunk' : 'Add Trunk'}</h1>
        <div className="ml-auto flex items-center gap-2">
          {isEdit && (
            <button
              onClick={() => { if (confirm('Delete this trunk? Its DID inbound routes are removed too — inbound to those numbers stops until you re-provision. This cannot be undone.')) del.mutate(); }}
              disabled={del.isPending}
              className="btn-ghost inline-flex items-center gap-1 text-danger">
              <Trash2 size={14} /> {del.isPending ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {tabs.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-4 py-2 text-sm ${tab === v ? 'border-b-2 border-primary text-primary' : 'text-textSecondary hover:text-textPrimary'}`}>
            {l}
          </button>
        ))}
      </div>

      {isEdit && existing.isLoading && !existing.data ? (
        <div className="card p-6 text-center text-textSecondary">Loading trunk…</div>
      ) : isEdit && existing.isError ? (
        <div className="card p-6 text-center text-danger">Couldn't load this trunk. Check the server is reachable.</div>
      ) : (
        <>
          {tab === 'general' && <GeneralTab form={form} set={set} preset={preset} tenants={tenants.data || []} />}
          {tab === 'dids' && <DidsTab form={form} set={set} />}
          {tab === 'sms' && <SmsTab form={form} set={set} preset={preset} isSuper={isSuper} />}
          {tab === 'options' && <OptionsTab form={form} set={set} />}
          {tab === 'test' && isEdit && id && <TestTab trunkId={id} />}
          {tab === 'e911' && isEdit && id && <E911Tab trunkId={id} />}
        </>
      )}
    </div>
  );
}

// Test panel — folded in from the retired Trunks page. Live gateway state +
// register/OPTIONS probes via /api/trunks-extended.
function TestTab({ trunkId }: { trunkId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [type, setType] = useState('register');
  const status = useQuery<any>({
    queryKey: ['trunk-status', trunkId],
    queryFn: () => fJson(`/api/trunks/${trunkId}/status`),
    refetchInterval: 15000,
  });
  const history = useQuery<any[]>({
    queryKey: ['trunk-tests', trunkId],
    queryFn: () => fJson(`/api/trunks-extended/${trunkId}/test-history`),
  });
  const run = useMutation({
    mutationFn: () => fJson(`/api/trunks-extended/${trunkId}/test`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test_type: type }),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['trunk-tests', trunkId] });
      toast(r.pass ? 'success' : 'error', r.pass ? `PASS (${r.analysis_code})` : `FAIL (${r.analysis_code})`);
    },
    onError: (e: any) => toast('error', e.message),
  });
  const refresh = useMutation({
    mutationFn: () => fJson(`/api/trunks-extended/${trunkId}/refresh-registration`, { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trunk-status', trunkId] }); toast('success', 'Re-register triggered'); },
    onError: (e: any) => toast('error', e.message),
  });
  const st = (status.data && status.data.state) || 'unknown';
  const stColor = st === 'registered' ? 'text-success' : st === 'noreg' ? 'text-warning' : st === 'failed' ? 'text-danger' : 'text-textSecondary';
  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-textSecondary">Gateway state:</span>
        <span className={`font-medium ${stColor}`}>{String(st).toUpperCase()}</span>
        <span className="text-xs text-textSecondary">— NOREG is correct for IP-auth trunks (e.g. Twilio)</span>
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label className="block text-sm text-textSecondary mb-1">Test type</label>
          <select className="input" value={type} onChange={e => setType(e.target.value)}>
            <option value="register">Register state</option>
            <option value="options">OPTIONS reachability</option>
            <option value="full">Full</option>
          </select>
        </div>
        <button onClick={() => run.mutate()} className="btn-primary inline-flex items-center gap-1"><PlayCircle size={14} /> Run test</button>
        <button onClick={() => refresh.mutate()} className="btn-ghost inline-flex items-center gap-1"><RefreshCw size={14} /> Force re-register</button>
      </div>
      <table className="table">
        <thead><tr><th>When</th><th>Type</th><th>Result</th><th>Code</th></tr></thead>
        <tbody>
          {(history.data || []).slice(0, 15).map((h: any) => (
            <tr key={h.test_id}>
              <td className="text-xs">{new Date(h.started_at).toLocaleString()}</td>
              <td className="text-xs">{h.test_type}</td>
              <td className={h.pass ? 'text-success' : 'text-danger'}>{h.pass ? 'PASS' : 'FAIL'}</td>
              <td className="text-xs">{h.analysis_code}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!(history.data || []).length && <div className="p-3 text-center text-textSecondary text-sm">No tests run yet.</div>}
    </div>
  );
}

// E911 / Kari's Law panel — folded in from the retired Trunks page.
function E911Tab({ trunkId }: { trunkId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<any>({});
  const list = useQuery<any[]>({
    queryKey: ['trunk-geo', trunkId],
    queryFn: () => fJson(`/api/trunks-extended/${trunkId}/emergency-locations`),
  });
  const add = useMutation({
    mutationFn: () => fJson(`/api/trunks-extended/${trunkId}/emergency-locations`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['trunk-geo', trunkId] }); setForm({});
      r?.twilio_address_sid
        ? toast('success', 'Location added + synced to Twilio')
        : toast('success', `Location added — Twilio sync pending: ${r?.twilio_sync_error || 'unknown'}`);
    },
    onError: (e: any) => toast('error', e.message),
  });
  const del = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/trunks-extended/${trunkId}/emergency-locations/${uuid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trunk-geo', trunkId] }),
  });
  const resync = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/trunks-extended/${trunkId}/emergency-locations/${uuid}/sync`, { method: 'POST' }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['trunk-geo', trunkId] });
      r?.twilio_address_sid ? toast('success', 'Address synced to Twilio') : toast('error', r?.twilio_sync_error || 'Sync failed');
    },
    onError: (e: any) => toast('error', e.message),
  });
  const apply = useMutation({
    mutationFn: (uuid: string) => fJson(`/api/trunks-extended/${trunkId}/emergency-locations/${uuid}/apply`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }),
    onSuccess: (r: any) => toast(r.ok ? 'success' : 'error', `E911 armed on ${r.applied} number(s)${r.failed ? `, ${r.failed} failed` : ''}`),
    onError: (e: any) => toast('error', e.message),
  });
  const sf = (k: string, v: any) => setForm({ ...form, [k]: v });
  return (
    <div className="card p-4 space-y-3">
      <p className="text-xs text-textSecondary inline-flex items-center gap-2">
        <MapPin size={14} /> Maps each DID to a dispatchable street address for 911 — required for Kari's Law / RAY BAUM compliance.
        Saving also creates the validated emergency Address in the department's Twilio account; "Arm numbers" then activates it on every number.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <input className="input" placeholder="Location name *" value={form.location_name || ''} onChange={e => sf('location_name', e.target.value)} />
        <input className="input" placeholder="DN / DID" value={form.trunk_dn || ''} onChange={e => sf('trunk_dn', e.target.value)} />
        <input className="input" placeholder="Provider URI" value={form.provider_uri || ''} onChange={e => sf('provider_uri', e.target.value)} />
        <input className="input col-span-3" placeholder="Street address" value={form.street_address || ''} onChange={e => sf('street_address', e.target.value)} />
        <input className="input" placeholder="City" value={form.city || ''} onChange={e => sf('city', e.target.value)} />
        <input className="input" placeholder="State / Region" value={form.state_region || ''} onChange={e => sf('state_region', e.target.value)} />
        <input className="input" placeholder="Postal code" value={form.postal_code || ''} onChange={e => sf('postal_code', e.target.value)} />
        <button className="btn-primary inline-flex items-center gap-1 col-span-3" onClick={() => add.mutate()} disabled={!form.location_name}>
          <Plus size={14} /> Add location
        </button>
      </div>
      <table className="table">
        <thead><tr><th>Name</th><th>DN</th><th>Address</th><th>Twilio</th><th></th></tr></thead>
        <tbody>
          {(list.data || []).map((g: any) => (
            <tr key={g.location_uuid}>
              <td>{g.location_name}</td>
              <td className="font-mono text-xs">{g.trunk_dn || '—'}</td>
              <td className="text-xs">{[g.street_address, g.city, g.state_region, g.postal_code].filter(Boolean).join(', ')}</td>
              <td className="text-xs">
                {g.twilio_address_sid ? (
                  <span className="text-success" title={g.twilio_address_sid}>synced</span>
                ) : (
                  <span className="text-danger" title={g.twilio_sync_error || ''}>
                    not synced
                    <button className="btn-ghost p-0.5 ml-1 text-xs underline" disabled={resync.isPending} onClick={() => resync.mutate(g.location_uuid)}>retry</button>
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap">
                {g.twilio_address_sid && (
                  <button className="btn-ghost p-1 text-xs" disabled={apply.isPending} title="Set this address as the active E911 address on every number in the department's Twilio account"
                    onClick={() => apply.mutate(g.location_uuid)}>Arm numbers</button>
                )}
                <button className="btn-ghost p-1 text-danger" onClick={() => del.mutate(g.location_uuid)}><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!(list.data || []).length && <div className="p-3 text-center text-textSecondary text-sm">No emergency locations yet.</div>}
    </div>
  );
}

// Shows the exact provider-side (Twilio) termination + origination values to paste, read live from the
// deployment via /api/trunks/twilio-recommended. Phase 1 of Twilio onboarding (Phase 2 automates it).
function TwilioRecommendedModal({ onClose }: { onClose: () => void }) {
  const rec = useQuery<any>({ queryKey: ['twilio-recommended'], queryFn: () => fJson('/api/twilio/recommended') });
  const [copied, setCopied] = useState('');
  const copy = (s: string, k: string) => { navigator.clipboard.writeText(s); setCopied(k); setTimeout(() => setCopied(''), 1200); };
  const d = rec.data;
  const Row = ({ label, value, k }: { label: string; value: string; k: string }) => (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="text-sm text-textSecondary">{label}</div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-mono text-sm">{value}</span>
        <button type="button" onClick={() => copy(value, k)} className="btn-ghost p-1" title="Copy">{copied === k ? <Check size={14} className="text-success" /> : <Copy size={14} />}</button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="card max-w-2xl w-full p-5 space-y-4 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Recommended Twilio trunk settings</h2>
          <button type="button" onClick={onClose} className="btn-ghost p-1"><X size={16} /></button>
        </div>
        <p className="text-xs text-textSecondary">Paste these into your Twilio Elastic SIP Trunk. Values are read from this Stratus deployment.</p>
        {!d && <div className="text-sm text-textSecondary">Loading…</div>}
        {d && <>
          <div className="border border-border rounded-md p-3">
            <div className="font-medium text-sm mb-1">Termination <span className="text-textSecondary font-normal">— outbound (Stratus → Twilio)</span></div>
            <Row label="Authentication → IP Access Control List" value={d.termination.ip_acl_cidr} k="acl" />
            <p className="text-xs text-textSecondary mt-1">Add an IP ACL with this CIDR (name it e.g. "{d.termination.ip_acl_name}"). Your Termination SIP URI (e.g. <span className="font-mono">yourname.pstn.twilio.com</span>) is what you enter in "Registrar / SIP Server (FQDN)" below.</p>
          </div>
          <div className="border border-border rounded-md p-3">
            <div className="font-medium text-sm mb-1">Origination <span className="text-textSecondary font-normal">— inbound (Twilio → Stratus)</span></div>
            <Row label="Origination SIP URI" value={d.origination.sip_uri} k="orig" />
            <div className="flex flex-wrap gap-4 text-xs text-textSecondary mt-1">
              <span>Priority: <span className="font-mono">{d.origination.priority}</span></span>
              <span>Weight: <span className="font-mono">{d.origination.weight}</span></span>
              <span>Enabled: <span className="font-mono">yes</span></span>
              <span>Registration: <span className="font-mono">off</span></span>
            </div>
          </div>
          <p className="text-xs text-textSecondary">Then assign your Twilio phone numbers to this trunk (Twilio → your trunk → Numbers). Coming soon: add a Twilio API key and Stratus can do all of this — including picking your numbers — automatically.</p>
        </>}
      </div>
    </div>
  );
}

// Two-step inbound-route picker: pick a CATEGORY, then (for categories that need one) a TARGET scoped
// to the trunk's department. Replaces the old single mega-list of every extension/VM/ring-group across
// all departments. Saves the same `value` encoding the backend already understands (end_call,
// accept_anyway, extension:<n>, voicemail:<n>, ring_group:<uuid>, queue:<uuid>, ivr:<uuid>,
// ai_receptionist:<extension_uuid>) so existing routes keep working.
const ROUTE_CATEGORIES: { key: string; label: string; type?: string }[] = [
  { key: 'end_call',        label: 'End Call' },
  { key: 'accept_anyway',   label: 'Accept Anyway (ignore office hours)' },
  { key: 'extension',       label: 'User',                  type: 'extension' },
  { key: 'voicemail',       label: 'Voicemail',             type: 'voicemail' },
  { key: 'ring_group',      label: 'Ring Group',            type: 'ring_group' },
  { key: 'queue',           label: 'Call Queue',            type: 'queue' },
  { key: 'ivr',             label: 'Digital Receptionist',  type: 'ivr' },
  { key: 'ai_receptionist', label: 'AI Voice Receptionist', type: 'ai_receptionist' },
];
function parseRouteCategory(v: string) {
  if (v === 'end_call' || v === 'accept_anyway') return v;
  const c = (v || '').split(':')[0];
  return ROUTE_CATEGORIES.some(x => x.key === c) ? c : '';
}
function InboundRoutePicker({ destinations, value, onChange }: { destinations: any[]; value: string; onChange: (v: string) => void }) {
  const [category, setCategory] = useState(() => parseRouteCategory(value) || 'end_call');
  // Keep the category in sync when the value loads/changes to a parseable route (e.g. editing a trunk).
  // An empty value (category has no targets) leaves the category as-is so the picker doesn't snap back.
  useEffect(() => { const c = parseRouteCategory(value); if (c) setCategory(c); }, [value]);
  const catDef = ROUTE_CATEGORIES.find(c => c.key === category) || ROUTE_CATEGORIES[0];
  const needsTarget = !!catDef.type;
  const targets = needsTarget ? (destinations || []).filter(d => d.type === catDef.type) : [];
  return (
    <div className="flex gap-2">
      <select className="input flex-1" value={category} onChange={e => {
        const key = e.target.value;
        setCategory(key);
        const c = ROUTE_CATEGORIES.find(x => x.key === key)!;
        if (!c.type) { onChange(key); return; }
        const first = (destinations || []).find(d => d.type === c.type);
        onChange(first ? first.value : '');
      }}>
        {ROUTE_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
      {needsTarget && (
        <select className="input flex-1" value={value} onChange={e => onChange(e.target.value)}>
          {targets.length === 0 && <option value="">— none in this department —</option>}
          {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      )}
    </div>
  );
}

function GeneralTab({ form, set, preset, tenants }: any) {
  const [showPwd, setShowPwd] = useState(false);
  const [showRec, setShowRec] = useState(false);
  // Real call destinations, scoped to the trunk's selected department (so no cross-department clutter).
  const destinations = useQuery<any[]>({
    queryKey: ['ch-destinations', form.domain_uuid || 'all'],
    queryFn: () => fJson('/api/call-handling/destinations' + (form.domain_uuid ? `?domain_uuid=${encodeURIComponent(form.domain_uuid)}` : '')),
  });
  return (
    <div className="card p-6 space-y-5">
      {/* Top row */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Display Name *">
          <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
            placeholder="e.g. Twilio Main Trunk" />
          <NameHint name={form.name} />
        </Field>
        <Field label="Inbound default route">
          <InboundRoutePicker destinations={destinations.data || []} value={form.default_route} onChange={(v) => set('default_route', v)} />
        </Field>
        <Field label="Department / Tenant">
          <select className="input" value={form.domain_uuid || ''} onChange={e => set('domain_uuid', e.target.value)}>
            <option value="">— Auto (first department) —</option>
            {(tenants || []).map((t: any) => (
              <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>
            ))}
          </select>
        </Field>
      </div>

      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={!!form.create_outbound_rule} onChange={e => set('create_outbound_rule', e.target.checked)} />
        Create an outbound rule for this SIP Trunk
      </label>

      {/* Voip Provider */}
      <div>
        <div className="font-medium border-b border-border pb-1 mb-3">Voip Provider</div>
        <p className="text-sm text-textSecondary">
          Follow the <a href={preset.config_guide_url || '#'} target="_blank" rel="noreferrer" className="text-primary hover:underline">{preset.label} Configuration Guide</a>
          {preset.whitelist_ip && (
            <>. Use this IP for Whitelisting <span className="font-mono">{preset.whitelist_ip}</span> <button onClick={() => navigator.clipboard.writeText(preset.whitelist_ip)} className="text-primary"><Copy size={12} className="inline" /></button></>
          )}
        </p>
        <button type="button" onClick={() => setShowRec(true)} className="btn-ghost text-sm inline-flex items-center gap-1 mt-2">
          <Info size={14} /> Recommended termination &amp; origination settings
        </button>
        {showRec && <TwilioRecommendedModal onClose={() => setShowRec(false)} />}
      </div>

      {/* Account Details */}
      <div>
        <div className="font-medium border-b border-border pb-1 mb-3">Account Details</div>
        <Field label="Main Trunk Number *">
          <input className="input" value={form.main_trunk_number} onChange={e => set('main_trunk_number', e.target.value)} onBlur={e => set('main_trunk_number', toE164(e.target.value))} placeholder="+18135910130" />
        </Field>
        <p className="text-xs text-warning mt-1 flex items-start gap-1">
          ⚠ DID numbers must be in e164 format: If your number is (813) 591-0130 enter it as +18135910130.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Authentication ID (SIP User ID)">
            <input className="input font-mono" value={form.auth_id} onChange={e => set('auth_id', e.target.value)} />
          </Field>
          <Field label="Authentication password">
            <div className="flex gap-2">
              <input type={showPwd ? 'text' : 'password'} className="input font-mono flex-1"
                value={form.auth_password} onChange={e => set('auth_password', e.target.value)} />
              <button onClick={() => setShowPwd(!showPwd)} className="btn-ghost p-1">{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
          </Field>
        </div>
      </div>

      {/* Server Details */}
      <div>
        <div className="font-medium border-b border-border pb-1 mb-3">Server Details</div>
        <div className="grid grid-cols-[1fr_120px_auto] gap-3 items-end">
          <Field label="Registrar / SIP Server (FQDN) *">
            <input className="input font-mono" value={form.registrar} onChange={e => set('registrar', e.target.value)} placeholder={preset.registrar_placeholder} />
          </Field>
          <Field label="Port *">
            <input className="input" type="number" value={form.registrar_port} onChange={e => set('registrar_port', parseInt(e.target.value) || 5060)} />
          </Field>
          <label className="text-sm flex items-center gap-2 pb-2">
            <input type="checkbox" checked={!!form.auto_discovery} onChange={e => set('auto_discovery', e.target.checked)} />
            Auto discovery
          </label>
        </div>
      </div>
    </div>
  );
}

function DidsTab({ form, set }: any) {
  const toast = useToast();
  const [adding, setAdding] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  // Inline number purchase (#505) — system-admin only (backend enforced); bills real money.
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyAreaCode, setBuyAreaCode] = useState('');
  const [buyFound, setBuyFound] = useState<any[] | null>(null);
  const [buyPick, setBuyPick] = useState('');
  const [buySearching, setBuySearching] = useState(false);
  const [buying, setBuying] = useState(false);
  // Destinations to route each DID to (scoped to this trunk's department).
  const destinations = useQuery<any[]>({
    queryKey: ['ch-destinations', form.domain_uuid],
    queryFn: () => fJson('/api/call-handling/destinations' + (form.domain_uuid ? `?domain_uuid=${encodeURIComponent(form.domain_uuid)}` : '')),
  });
  const destList = destinations.data || [];
  const routeLabel = (route: string) => {
    if (!route || route === 'end_call') return '';
    const m = destList.find((d) => d.value === route);
    return m ? m.label : route;
  };
  function updateDid(i: number, patch: any) {
    const next = [...(form.dids || [])];
    next[i] = { ...next[i], ...patch };
    set('dids', next);
  }
  function addDid() {
    if (!adding) return;
    set('dids', [...(form.dids || []), { number: adding, name: '', route: '' }]);
    setAdding('');
  }
  function delDid(i: number) {
    const next = [...(form.dids || [])];
    next.splice(i, 1);
    set('dids', next);
  }
  function bulkDelete() {
    if (picked.size === 0) return;
    const next = (form.dids || []).filter((_: any, i: number) => !picked.has(i));
    set('dids', next);
    setPicked(new Set());
  }
  function togglePick(i: number) {
    const next = new Set(picked);
    if (next.has(i)) next.delete(i); else next.add(i);
    setPicked(next);
  }
  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      // Accept CSV (number,name) OR plain newline-separated numbers
      const text = String(r.result || '');
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const next = lines.map(line => {
        const cols = line.split(',').map(c => c.trim());
        return { number: cols[0], name: cols[1] || '', route: '' };
      }).filter(d => d.number && /^[\d+*#]+$/.test(d.number));
      set('dids', [...(form.dids || []), ...next]);
    };
    r.readAsText(file);
    e.target.value = '';
  }
  async function buySearch() {
    setBuySearching(true); setBuyFound(null); setBuyPick('');
    try {
      const r = await fJson(`/api/integrations/twilio/available-numbers?domain_uuid=${encodeURIComponent(form.domain_uuid || '')}${buyAreaCode ? `&area_code=${buyAreaCode}` : ''}`);
      setBuyFound(r.numbers || []);
    } catch (e: any) {
      let msg = e.message; try { msg = JSON.parse(e.message).error || msg; } catch {}
      toast('error', msg);
    } finally { setBuySearching(false); }
  }
  async function buyNumber() {
    if (!buyPick) return;
    setBuying(true);
    try {
      const r = await fJson('/api/integrations/twilio/buy-number', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain_uuid: form.domain_uuid, phone_number: buyPick }),
      });
      set('dids', [...(form.dids || []), { number: r.phone_number, name: '', route: '' }]);
      toast('success', `Bought ${r.phone_number} (${r.source} account)${r.attached_to_trunk ? ' — on the SIP trunk' : ''}. Set its route and Save.`);
      setBuyOpen(false); setBuyFound(null); setBuyPick('');
    } catch (e: any) {
      let msg = e.message; try { msg = JSON.parse(e.message).error || msg; } catch {}
      toast('error', msg);
    } finally { setBuying(false); }
  }
  return (
    <div className="card p-4 space-y-3">
      <p className="text-xs text-warning flex items-start gap-1">
        ⚠ DID numbers must be in e164 format: If your number is (813) 591-0130 enter it as +18135910130.
      </p>
      <p className="text-xs text-textSecondary">
        Set where each DID routes under <strong>Routes to</strong>. On Save, each DID with a
        destination gets an inbound rule so calls to it ring the right place. Leave it blank to keep
        an existing assignment (e.g. a number already pointed at a receptionist).
      </p>
      <div className="flex items-center gap-2">
        <input className="input font-mono flex-1 max-w-xs" placeholder="+18135910130" value={adding}
               onChange={e => setAdding(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDid(); } }} />
        <button onClick={addDid} className="btn-primary inline-flex items-center gap-1"><Plus size={14} /> Add</button>
        <label className="btn-ghost inline-flex items-center gap-1 cursor-pointer">
          Import
          <input type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} />
        </label>
        <button onClick={bulkDelete} disabled={picked.size === 0} className="btn-ghost text-danger inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>
        <button type="button" onClick={() => { setBuyOpen(!buyOpen); setBuyFound(null); setBuyPick(''); }} className="btn-ghost inline-flex items-center gap-1">
          <Plus size={14} /> Buy number
        </button>
      </div>
      {buyOpen && (
        <div className="border border-border rounded-lg p-3 space-y-3">
          <p className="text-xs text-textSecondary">Buys a real number on the department's Twilio account (or the master account if it has none) — system admins only, billed monthly. It attaches to the SIP trunk automatically when one exists on that account.</p>
          <div className="flex items-center gap-2">
            <input className="input font-mono max-w-[140px]" maxLength={3} placeholder="Area code" value={buyAreaCode}
                   onChange={e => { setBuyAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3)); setBuyFound(null); setBuyPick(''); }} />
            <button type="button" className="btn-ghost" disabled={buySearching} onClick={buySearch}>{buySearching ? 'Searching…' : 'Search numbers'}</button>
            {buyPick && <button type="button" className="btn-primary" disabled={buying} onClick={buyNumber}>{buying ? 'Buying…' : `Buy ${buyPick}`}</button>}
          </div>
          {buyFound && buyFound.length > 0 && (
            <div className="border border-border rounded-lg divide-y divide-border max-h-56 overflow-auto">
              {buyFound.map((n: any) => (
                <button type="button" key={n.phone_number}
                  className={`w-full text-left px-3 py-2 text-sm flex justify-between items-center hover:bg-elevated ${buyPick === n.phone_number ? 'bg-primary/15' : ''}`}
                  onClick={() => setBuyPick(n.phone_number)}>
                  <span className="font-mono">{n.phone_number}</span>
                  <span className="text-xs text-textSecondary">{[n.locality, n.region].filter(Boolean).join(', ')}</span>
                </button>
              ))}
            </div>
          )}
          {buyFound && buyFound.length === 0 && <div className="text-xs text-textSecondary">No available numbers — try a different area code.</div>}
        </div>
      )}
      <table className="table">
        <thead><tr><th></th><th>DID Number</th><th>Name</th><th>Routes to</th><th></th></tr></thead>
        <tbody>
          {(form.dids || []).map((d: any, i: number) => (
            <tr key={i}>
              <td><input type="checkbox" checked={picked.has(i)} onChange={() => togglePick(i)} /></td>
              <td className="font-mono whitespace-nowrap">{d.number}</td>
              <td>
                <input className="input py-1 text-sm min-w-[120px]" value={d.name || ''} placeholder="—"
                       onChange={e => updateDid(i, { name: e.target.value })} />
              </td>
              <td className="min-w-[280px]">
                <InboundRoutePicker destinations={destList} value={d.route || ''}
                                    onChange={(v) => updateDid(i, { route: v, assigned_to: routeLabel(v) })} />
              </td>
              <td><button onClick={() => delDid(i)} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {!form.dids?.length && <div className="p-4 text-center text-textSecondary text-sm">No DIDs yet.</div>}
    </div>
  );
}

function SmsTab({ form, set, preset, isSuper }: any) {
  const [showToken, setShowToken] = useState(false);
  const [lane, setLane] = useState<'quick' | 'legacy' | 'dedicated'>('legacy');
  // Department SMS state (real webhook + whether creds are already saved). Lets
  // the tab prefill the Account SID and show the live inbound webhook URL.
  const cfg = useQuery<any>({
    queryKey: ['sms-config', form.domain_uuid],
    queryFn: () => fJson(`/api/sms/config?domain_uuid=${form.domain_uuid}`),
    enabled: !!form.domain_uuid && isSuper,
  });
  const connected = !!cfg.data?.twilio_connected;
  const webhook = cfg.data?.webhook_url || '';
  // Prefill the (non-secret) Account SID from saved dept credentials, once.
  useEffect(() => {
    if (cfg.data?.account_sid && !form.sms_account_sid) set('sms_account_sid', cfg.data.account_sid);
  }, [cfg.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isSuper) {
    return (
      <div className="card p-6 max-w-2xl text-sm text-textSecondary">
        SMS setup for a trunk is restricted to system administrators.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <SmsLanePicker lane={lane} onChange={setLane} />

      {lane === 'quick' && <QuickLane domainUuid={form.domain_uuid} webhook={webhook} onDone={() => cfg.refetch()} />}
      {lane === 'dedicated' && <DedicatedLane domainUuid={form.domain_uuid} onDone={() => cfg.refetch()} />}
      {lane === 'legacy' && (
        <div className="card p-6 space-y-4">
          <label className="text-sm flex items-center gap-2 font-medium">
            <input type="checkbox" checked={!!form.sms_enabled} onChange={e => set('sms_enabled', e.target.checked)} />
            Enable SMS (client's own Twilio credentials)
          </label>
          <p className="text-sm text-textSecondary">
            Follow the <a href={preset.config_guide_url || '#'} className="text-primary hover:underline">{preset.label} Configuration Guide</a>.
            Saving auto-configures the SMS webhook on your Twilio number(s) — no manual setup in Twilio.
          </p>
          <p className="text-xs text-warning flex items-start gap-1">
            ⚠ Add the trunk's DID number(s) (DID Numbers tab) in E.164 for sending and receiving SMS.
          </p>
          <Field label="Webhook (auto-configured in Twilio on save)">
            {webhook ? (
              <div className="flex items-center gap-2">
                <span className="text-primary text-xs break-all flex-1">{webhook}</span>
                <button className="btn-ghost p-1" title="Copy webhook URL" onClick={() => navigator.clipboard.writeText(webhook)}><Copy size={14} /></button>
              </div>
            ) : (
              <span className="text-xs text-textSecondary">Generated when you enable SMS and save.</span>
            )}
          </Field>
          <Field label="Account SID *">
            <input className="input font-mono" value={form.sms_account_sid} onChange={e => set('sms_account_sid', e.target.value)} disabled={!form.sms_enabled} placeholder="AC…" />
          </Field>
          <Field label={connected ? 'Auth Token (saved — leave blank to keep)' : 'Auth Token *'}>
            <div className="flex gap-2">
              <input type={showToken ? 'text' : 'password'} className="input font-mono flex-1"
                value={form.sms_auth_token} onChange={e => set('sms_auth_token', e.target.value)} disabled={!form.sms_enabled}
                placeholder={connected ? '•••••••• (using saved credentials)' : ''} />
              <button onClick={() => setShowToken(!showToken)} className="btn-ghost p-1">{showToken ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
          </Field>
          <div className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.sms_enable_caller_id_match} onChange={e => set('sms_enable_caller_id_match', e.target.checked)} disabled={!form.sms_enabled} />
            Enable Caller ID Number Matching ⓘ
            <input type="number" className="input max-w-[80px]" value={form.sms_caller_id_match_digits} onChange={e => set('sms_caller_id_match_digits', parseInt(e.target.value) || 3)} disabled={!form.sms_enabled} />
            <span>Digits</span>
          </div>
        </div>
      )}

      <CancelSms domainUuid={form.domain_uuid} fromNumber={cfg.data?.from_number} smsEnabled={cfg.data?.enabled} onDone={() => cfg.refetch()} />
      <MasterConfigPanel />
    </div>
  );
}

function OptionsTab({ form, set }: any) {
  const [open, setOpen] = useState({ config: true, security: true, e164: false, reformat: false, cid: false, codec: false });

  return (
    <div className="card p-4 space-y-3">
      <Section title="Configuration" open={open.config} onToggle={() => setOpen({ ...open, config: !open.config })}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Limit to">
            <select className="input" value={form.limit_to} onChange={e => set('limit_to', e.target.value)}>
              <option value="system_wide">System Wide</option>
              <option value="department">Department</option>
            </select>
          </Field>
          <Field label="Number of sim calls per trunk *">
            <input type="number" className="input" min={1} max={500} value={form.sim_calls} onChange={e => set('sim_calls', parseInt(e.target.value) || 10)} />
          </Field>
          <div className="space-y-1">
            <Toggle label="Allow inbound calls" v={!!form.allow_inbound} on={v => set('allow_inbound', v)} />
            <Toggle label="Include diversion header support" v={!!form.diversion_header} on={v => set('diversion_header', v)} />
          </div>
          <div className="space-y-1">
            <Toggle label="Allow outbound calls" v={!!form.allow_outbound} on={v => set('allow_outbound', v)} />
            <Toggle label="Allow carrier-side transfers (SIP REFER) ⓘ" v={!!form.sip_refer_xfer} on={v => set('sip_refer_xfer', v)} />
          </div>
        </div>
      </Section>
      <Section title="Authentication & Security" open={open.security} onToggle={() => setOpen({ ...open, security: !open.security })}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Trunk type">
            <select className="input" value={form.gateway_type} onChange={e => set('gateway_type', e.target.value)}>
              <option value="Provider">Provider (PSTN carrier)</option>
              <option value="Bridge">Bridge (PBX-to-PBX)</option>
              <option value="Custom">Custom</option>
            </select>
          </Field>
          <Field label="Inbound matching strategy">
            <select className="input" value={form.matching_strategy_enum} onChange={e => set('matching_strategy_enum', e.target.value)}>
              <option value="MatchAnyFields">Match any field (IP / From / To)</option>
              <option value="MatchAllFields">Match all fields</option>
              <option value="ByAuthCredentials">By auth credentials only</option>
            </select>
          </Field>
          <Field label="Require authentication for">
            <select className="input" value={form.require_auth_for_enum} onChange={e => set('require_auth_for_enum', e.target.value)}>
              <option value="Both">Inbound and outbound</option>
              <option value="Inbound">Inbound only</option>
              <option value="Outbound">Outbound only</option>
              <option value="Nothing">Don't require auth (IP only)</option>
            </select>
          </Field>
          <Field label="SRTP mode">
            <select className="input" value={form.srtp_mode_enum} onChange={e => set('srtp_mode_enum', e.target.value)}>
              <option value="SRTPDisabled">Disabled</option>
              <option value="SRTPPreferred">Preferred</option>
              <option value="SRTPRequired">Required</option>
            </select>
          </Field>
          <Field label="IP in REGISTER Contact">
            <select className="input" value={form.ip_in_registration_contact} onChange={e => set('ip_in_registration_contact', e.target.value)}>
              <option value="External">External (NAT) IP</option>
              <option value="Internal">Internal IP</option>
              <option value="Auto">Auto</option>
            </select>
          </Field>
          <div className="space-y-1 pt-6">
            <Toggle label="Disable call transfers (REFER) on this trunk" v={!!form.disable_call_transfer} on={v => set('disable_call_transfer', v)} />
          </div>
        </div>
      </Section>
      <Section title="Convert inbound caller ID to E164 number format" open={open.e164} onToggle={() => setOpen({ ...open, e164: !open.e164 })}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Country">
            <select className="input"><option>United States (+1)</option></select>
          </Field>
          <Field label="International Dial Code">
            <input className="input font-mono" defaultValue="011" />
          </Field>
        </div>
      </Section>
      <Section title="Reformat inbound or outbound caller ID" open={open.reformat} onToggle={() => setOpen({ ...open, reformat: !open.reformat })}>
        <p className="text-xs text-textSecondary">{'Add regex Match → Build rules to rewrite caller IDs (uses standard <Match>regex</Match><Build>$1...</Build> schema).'}</p>
      </Section>
      <Section title="Caller ID Control" open={open.cid} onToggle={() => setOpen({ ...open, cid: !open.cid })}>
        <p className="text-xs text-textSecondary">Per-trunk OutboundCallerID override (route-level overrides this).</p>
      </Section>
      <Section title="Codec priority" open={open.codec} onToggle={() => setOpen({ ...open, codec: !open.codec })}>
        <p className="text-xs text-textSecondary">Two priority lists (LAN / WAN). Default order: PCMU → OPUS → G722 → PCMA → G729.</p>
      </Section>
    </div>
  );
}

function Section({ title, open, onToggle, children }: any) {
  return (
    <div className="border border-border rounded">
      <button onClick={onToggle} className="w-full text-left px-3 py-2 flex items-center gap-2 font-medium hover:bg-elevated">
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title}
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}
// Live feedback under the Display Name field. The display name can be anything
// (it is stored verbatim as the trunk's description); FreeSWITCH needs a
// space-free identifier, so we show the user what that identifier will become.
function NameHint({ name }: { name: string }) {
  if (!name) {
    return <p className="text-xs text-textSecondary mt-1">A friendly label — your company or carrier name. Spaces are fine here.</p>;
  }
  const slug = gwSlug(name);
  const changed = slug !== name;
  return (
    <p className={`text-xs mt-1 ${changed ? 'text-warning' : 'text-textSecondary'}`}>
      {changed
        ? `⚠ FreeSWITCH gateway ID will be saved as "${slug || 'trunk'}" — spaces and special characters aren't allowed in the technical ID. Your display name "${name}" is kept exactly as typed.`
        : `FreeSWITCH gateway ID: "${slug}"`}
    </p>
  );
}

function Field({ label, children }: any) { return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>; }
function Toggle({ label, v, on }: any) { return <label className="text-sm flex items-center gap-2 py-1"><input type="checkbox" checked={v} onChange={e => on(e.target.checked)} /> {label}</label>; }
